import {useEffect, useRef, useState, useCallback} from 'react'
import {
  RightPanel,
  StyledBoxDescription,
  SaveButton,
  CancelButton,
  NewIcon,
  CenteredErrorContainer,
  SaveAndExitButton,
  NavigationBox,
  NavigationItem,
  StickyFooterBox
} from './ProjectDetailStyle'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import ProjectDescriptionComponent from './ProjectDescription/ProjectDescriptionComponent'
import JurisdictionComponent from './Jurisdiction/JurisdictionComponent'
import ProjectSectionAccordion from './common/ProjectSectionAccordion/ProjectSectionAccordianComponent.js'
import SectionGroupHeader from './common/SectionGroupHeader'
import PermissionsHeader from './common/PermissionsHeader/PermissionsHeader.js'
import DataAggregation from './DataAggregation/DataAggregation'
import IndividualProfileComponent from './IndividualProfile/IndividualProfileComponent'
import IndividualHealthFilterComponent from './IndividualHealthFilter/IndividualHealthFilterComponent'
import FinancialWellbeingComponent from './FinancialWellbeing/FinancialWellbeingComponent'
import HomeAndHouseholdFilterComponent from './HomeAndHouseholdFilters/HomeAndHouseholdFilterComponent'
import ResourceAccessBarriersComponent from './ResourceAccessBarriers/ResourceAccessBarriersComponent'
import CommunitySupportComponent from './CommunitySupport/CommunitySupportComponent'
import EnvironmentalExposureComponent from './EnvironmentalExposure/EnvironmentalExposureComponent'
import TrendAnalysisComponent from './TrendAnalysis/TrendAnalysis'
import LoadingIcon from '../common/LoadingIcon/LoadingIcon'
import BreadcrumbsComponent from '../common/BreadcrumbsRoute/BreadcrumbComponent'
import {useParams, useNavigate} from 'react-router-dom'
import {StyledTypography} from '../AdminPanelStyling'
import {StyledBackButton, StyledBackIcon} from '../ProjectList/ProjectListLandingStyles'
import ErrorNotificationComponent from './ErrorNotification/ErrorNotificationComponent'
import InfoNotificationComponent from './InfoNotification/InfoNotificationComponent'
import PopupComponent from './PopupComponent/PopupComponent'
import {ProjectDetailsRow} from './ProjectDescription/ProjectDescriptionStyle'
import {saveCreateProject} from './Helper/CreateProject'
import UserManagementComponent from '../UserManagement/UserManagementComponent'
import SnackbarMessage from '../common/Snackbar/SnackbarMessages.json'
import SnackbarComponent from '../common/Snackbar/SnackbarComponent'
import DataRefetchOnError from '../common/DataFetchError/DataRefetchOnError'
import ErrorComponent from '../../components/Error/ErrorComponent'
import getStatusCode from '../common/Helpers/getStatusCode'
import {useProjectDetails} from '../../store/hooks/projectDetails'
import {useCreateProject} from '../../store/hooks/createProject'
import {saveUpdateProject} from './Helper/UpdateProject'
import {resetAll} from './Helper/ResetAll'
import {handleNavigation} from './Helper/HandleNavigation'
import {
  selectedJurisdictionChanged,
  selectedDataAggregationChanged,
  projectDescriptionChanged,
  selectedUseCasesChanged,
  projectNameChanged,
  selectedTrendAnalysisChanged,
  selectedChatbotChanged,
  selectedExportToggleChanged,
  selectedBenefitsChanged,
  selectedWorkforcesChanged,
  selectedPOIChanged,
  selectedPersonaChanged,
  selectedChatbotExportChanged,
  selectedChatbotCopyChanged
} from './Helper/CompareChanges'
import {checkDetailsUpdated} from './Helper/CheckDetailsUpdated'
import {useUpdateProject} from '../../store/hooks/updateProject'
import {useGetGroupPermissionDetails} from '../api/getGroupProjectList'
import {useGetGroupDetails} from '../api/getProjectDetails'
import loadingTitles from '../../appConfig/loadingTitles.config.json'
import PropTypes from 'prop-types'
import {useConfig} from '../../store/hooks/config'
import ChatbotComponent from './chatbot/ToggleChatbot'
import ChatbotExport from './chatbot/ChatbotExport'
import ChatbotCopy from './chatbot/ChatbotCopy'
import ExportPermissionComponent from './ExportPermissionComponent/ExportPermissionComponent'
import PointOfInterest from './PointOfInterest/PointOfInterest'
import Workforces from './Workforces'
import Benefits from './Benefits'
import UseCases from './UseCases'
import ConfirmationDialog from './UseCases/ConfirmationDialog'
import UseCaseNotificationAlert from './UseCases/UseCaseNotificationAlert'
import {StyledFlexBox, StyledNotificationInfoOutlinedIcon, StyledNotificationTextBox} from './UseCases/UseCaseStyles'
import {getInitialUseCaseConfig, getSelectedUseCases, handleUpdateSelectedUseCases} from './Helper/UseCaseHelper'
import PersonaFlag from './PersonaFlag/PersonaFlag'

// Helper function to build all filters from config
const buildAllFiltersFromConfig = (filterConfig) => {
  const allFilters = []
  if (!filterConfig?.data) return allFilters
  for (const section of filterConfig.data) {
    for (const filter of section.filters) {
      allFilters.push({
        title: filter.name,
        FILTER_KEY: filter.key,
        FILTER_ID: filter.key,
        IS_ACTIVE: true
      })
    }
  }
  return allFilters
}

const ProjectDetailsComponent = ({isAdminPanelOpen, setIsAdminPanelOpen}) => {
  ProjectDetailsComponent.propTypes = {
    isAdminPanelOpen: PropTypes.bool.isRequired,
    setIsAdminPanelOpen: PropTypes.func.isRequired
  }

  useEffect(() => {
    setIsAdminPanelOpen(true)
  }, [setIsAdminPanelOpen])

  const navigate = useNavigate()
  const {clientId, projectId} = useParams()
  const {groupPermissionListDetails, loadingGroupPermission, refetchGroupPermission, errorGroupPermissions} =
    useGetGroupPermissionDetails(projectId, clientId)
  const {group, loadingGroups, refetchGroups, errorGroups} = useGetGroupDetails(clientId)
  const [createProject, setCreateProject] = useState(false)
  const [currentGroupName, setCurrentGroupName] = useState('')
  const [currentDescription, setCurrentDescription] = useState('')
  const [currentJurisdiction, setCurrentJurisdiction] = useState([])
  const [currentDataAggregation, setCurrentDataAggregation] = useState([])
  const [currentFilters, setCurrentFilters] = useState([])
  const [currentUseCases, setCurrentUseCases] = useState([])
  const [currentBenefits, setCurrentBenefits] = useState([])
  const [currentWorkforces, setCurrentWorkforces] = useState([])
  const [currentPOI, setCurrentPOI] = useState([])
  const [projectName, setProjectName] = useState('')
  const [projectDescription, setProjectDescription] = useState('')
  const [renderUserView, setRenderUserView] = useState(false)
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true)
  const [showPopupOnNav, setShowPopupOnNav] = useState(false)
  const [inputDisabled, setInputDisabled] = useState(true)
  const [errorTextEnabled, setErrorTextEnabled] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [filterToBeDeleted, setFilterToBeDeleted] = useState({})
  const [affectedUseCases, setAffectedUseCases] = useState([])
  const [showUseCaseConfirmationDialog, setShowUseCaseConfirmationDialog] = useState(false)

  // Accordion state management - controls which sections are expanded
  // Using an object to track each section's expanded state independently
  const [expandedSections, setExpandedSections] = useState({
    jurisdictions: false,
    dataGranularity: false,
    individualProfile: false,
    individualHealth: false,
    financialWellbeing: false,
    homeAndHousehold: false,
    resourceAccessBarriers: false,
    communitySupport: false,
    environmentalExposure: false,
    benefitPrograms: false,
    pointOfInterests: false,
    commonUsecases: false,
    behavioralPersona: false,
    trendAnalysis: false,
    export: false,
    healthprismAgent: false
  })

  // Handler for individual accordion changes
  // Uses currying pattern: handleAccordionChange('sectionKey') returns event handler
  const handleAccordionChange = useCallback(
    (sectionKey) => (event, isExpanded) => {
      setExpandedSections((prev) => ({
        ...prev,
        [sectionKey]: isExpanded
      }))
    },
    []
  )

  // Check if all sections are expanded
  const areAllSectionsExpanded = Object.values(expandedSections).every(Boolean)

  // Toggle all sections expand/collapse
  const handleToggleAllSections = useCallback(() => {
    const newState = !areAllSectionsExpanded
    setExpandedSections({
      jurisdictions: newState,
      dataGranularity: newState,
      individualProfile: newState,
      individualHealth: newState,
      financialWellbeing: newState,
      homeAndHousehold: newState,
      resourceAccessBarriers: newState,
      communitySupport: newState,
      environmentalExposure: newState,
      benefitPrograms: newState,
      pointOfInterests: newState,
      commonUsecases: newState,
      behavioralPersona: newState,
      trendAnalysis: newState,
      export: newState,
      healthprismAgent: newState
    })
  }, [areAllSectionsExpanded])

  const {
    loadingStates,
    errorLoadingStates,
    filters,
    benefits,
    workforces,
    useCases,
    isCompleted,
    projectNamesList,
    jurisdiction,
    loadingUpdateProject,
    error,
    filterDetailsError,
    filterDetailsLoading,
    clientName,
    navigationProps,
    createProjectNavProps,
    setNavigationProps,
    setCreateProjectNavProps,
    setClientName,
    setIsCompleted,
    setProjectNamesList,
    updateProjectList,
    setHasUsers,
    personaFlag,
    setPersonaFlag,
    trendAnalysisFlag,
    setTrendAnalysisFlag,
    chatbotFlag,
    setChatbotFlag,
    chatbotExportFlag,
    setChatbotExportFlag,
    chatbotCopyFlag,
    setChatbotCopyFlag,
    exportFlag,
    setExportFlag,
    pointOfInterestsData
  } = useProjectDetails()

  const {createProjectLoading, createProjectError, creategroup, saveCreateProjectDetails, handleCreate} =
    useCreateProject()
  const {saveUpdateProjectDetails, handleUpdate} = useUpdateProject()
  const [open, setOpen] = useState(false)
  const [popupStyle, setPopupStyle] = useState('')
  const [goBack, setGoBack] = useState(false)
  const {dataAggregationConfig, filterConfig} = useConfig() || {}
  const [navigateToProjectList, setNavigateToProjectList] = useState(false)
  const [useCaseConfigData, setUseCaseConfigData] = useState([])

  useEffect(() => {
    const handleProjectDetails = () => {
      if (
        projectId &&
        !loadingGroupPermission &&
        !errorGroupPermissions &&
        groupPermissionListDetails?.groupPermissionList &&
        groupPermissionListDetails.groupPermissionList.length > 0
      ) {
        const {groupPermissionList} = groupPermissionListDetails
        const groupDetails = groupPermissionList.find((option) => option.GROUP_ID === Number.parseInt(projectId))
        setUseCaseConfigData(getInitialUseCaseConfig(useCases, groupDetails.USECASE))
        setCurrentGroupName(groupDetails.GROUP_NAME)
        settingCurrentDataAggregation(groupPermissionList, groupDetails)
        settingFilterValues(groupPermissionList, groupDetails)
        setPersonaFlag(groupDetails?.PERSONA_FLAG || [0])
        setTrendAnalysisFlag(groupDetails?.TREND_ANALYSIS_FLAG || [0])
        setExportFlag(groupDetails?.EXPORT_FLAG || [0])
        setChatbotFlag(groupDetails?.CHATBOT_FLAG || [0])
        setChatbotExportFlag(groupDetails?.CHATBOT_EXPORT_FLAG || [0])
        setChatbotCopyFlag(groupDetails?.CHATBOT_COPY_FLAG || [0])
      }
    }
    const handleCreateProject = () => {
      if (!projectId) {
        setUseCaseConfigData(getInitialUseCaseConfig(useCases, []))
        setCreateProject(true)
        setExportFlag([0])
        setPersonaFlag([0])
        setTrendAnalysisFlag([0])
        setChatbotFlag([0])
        setChatbotExportFlag([0])
        setChatbotCopyFlag([0])
      }
    }
    handleProjectDetails()
    handleCreateProject()
  }, [
    groupPermissionListDetails,
    projectId,
    loadingGroupPermission,
    loadingUpdateProject,
    errorGroupPermissions,
    setPersonaFlag,
    setTrendAnalysisFlag,
    setExportFlag,
    setChatbotFlag,
    setChatbotExportFlag,
    setChatbotCopyFlag,
    useCases
  ])

  useEffect(() => {
    setProjectName(currentGroupName)
  }, [currentGroupName])

  useEffect(() => {
    setProjectDescription(currentDescription)
  }, [currentDescription])

  useEffect(() => {
    //  if access with url location state will be null and group api will take time to fetch
    if (!loadingGroups && group?.groupList) {
      const {groupList} = group
      const curGroup =
        projectId && groupList.GROUPList.filter((option) => option.GROUP_ID === Number.parseInt(projectId))
      const clientDetails = {clientId, projectId, currentGroupName}
      handleNavigation(groupList.CLIENT_NAME, setNavigationProps, setCreateProjectNavProps, clientDetails)
      setClientName(groupList.CLIENT_NAME)
      setIsCompleted(curGroup?.[0]?.IS_COMPLETED)
      setProjectNamesList(groupList.GROUPList.map((element) => element.GROUP_NAME))
      const getGroup = groupList.GROUPList.find((element) => element.GROUP_ID === projectId)
      projectId && setHasUsers(Number.parseInt(getGroup?.TOTAL_ACTIVE_USERS) > 0 || getGroup?.TOTAL_ARCHIVE_USERS > 0)
    }
  }, [
    group,
    clientName,
    currentGroupName,
    updateProjectList,
    loadingGroups,
    clientId,
    projectId,
    setClientName,
    setCreateProjectNavProps,
    setHasUsers,
    setIsCompleted,
    setNavigationProps,
    setProjectNamesList
  ])

  const settingCurrentDataAggregation = (groupPermissionList, groupDetails) => {
    groupPermissionList?.[0]?.DESCRIPTION && setCurrentDescription(groupDetails.DESCRIPTION)
    groupPermissionList?.[0]?.JURISDICTION?.length >= 0 && setCurrentJurisdiction(groupDetails.JURISDICTION)
    groupPermissionList?.[0]?.DATA_AGGREGATION?.length >= 0 && setCurrentDataAggregation(groupDetails.DATA_AGGREGATION)
  }
  const settingFilterValues = (groupPermissionList, groupDetails) => {
    if (groupPermissionList?.[0]?.FILTERS !== undefined) {
      setCurrentFilters(groupDetails.FILTERS || [])
    }
    groupPermissionList?.[0]?.USECASE?.length >= 0 && setCurrentUseCases(groupDetails.USECASE)
    groupPermissionList?.[0]?.BENEFITS?.length >= 0 && setCurrentBenefits(groupDetails?.BENEFITS || [])
    groupPermissionList?.[0]?.WORKFORCES?.length >= 0 && setCurrentWorkforces(groupDetails?.WORKFORCES || [])
    groupPermissionList?.[0]?.POINT_OF_INTEREST?.length >= 0 && setCurrentPOI(groupDetails.POINT_OF_INTEREST)
  }
  // kept loading simple
  const loading = isSaving || loadingGroupPermission || loadingGroups || loadingUpdateProject || createProjectLoading
  const errorInFetching = errorGroupPermissions || errorGroups

  const [selectedJurisdiction, setSelectedJurisdiction] = useState(
    jurisdiction.filter((option) =>
      currentJurisdiction.map((id) => Number.parseInt(id)).includes(Number.parseInt(option.STATE_ID))
    )
  )
  const [selectedDataAggregation, setSelectedDataAggregation] = useState(
    dataAggregationConfig?.data?.filter((option) => currentDataAggregation.includes(option.key)) || []
  )

  const [selectedTrendAnalysis, setSelectedTrendAnalysis] = useState(trendAnalysisFlag)
  const [selectedPersona, setSelectedPersona] = useState(personaFlag)
  const [selectedChatbot, setSelectedChatbot] = useState(chatbotFlag)
  const [selectedExportToggle, setSelectedExportToggle] = useState(exportFlag)

  // New toggles for export and copy-paste permissions
  const [selectedChatbotExport, setSelectedChatbotExport] = useState(chatbotExportFlag || [0])
  const [selectedChatbotCopy, setSelectedChatbotCopy] = useState(chatbotCopyFlag || [0])

  const [selectedFilters, setSelectedFilters] = useState([])
  const [selectedUseCases, setSelectedUseCases] = useState([])
  const [updatedUseCases, setUpdatedUseCases] = useState([])
  const [selectedBenefits, setSelectedBenefits] = useState(
    benefits.filter((option) => currentBenefits && currentBenefits.length > 0 && currentBenefits.includes(option.id)) ||
      []
  )

  const [selectedWorkforces, setSelectedWorkforces] = useState(
    workforces.filter(
      (option) => currentWorkforces && currentWorkforces.length > 0 && currentWorkforces.includes(option.id)
    ) || []
  )

  const [selectedPOI, setSelectedPOI] = useState(
    pointOfInterestsData.filter((option) => currentPOI.includes(Number.parseInt(option.POI_ID)))
  )

  useEffect(() => {
    const allConfigFilters = buildAllFiltersFromConfig(filterConfig)

    const availableFilters = filters && filters.length > 0 ? filters : allConfigFilters

    const preSelectedFilters =
      availableFilters.filter(
        (option) => currentFilters && currentFilters.length > 0 && currentFilters.includes(option.FILTER_KEY)
      ) || []
    setSelectedFilters(preSelectedFilters)
    setSelectedUseCases(currentUseCases || [])
    setSelectedBenefits(
      benefits.filter(
        (option) => currentBenefits && currentBenefits.length > 0 && currentBenefits.includes(option.id)
      ) || []
    )
    setSelectedWorkforces(
      workforces.filter(
        (option) => currentWorkforces && currentWorkforces.length > 0 && currentWorkforces.includes(option.id)
      ) || []
    )
    setSelectedDataAggregation(
      dataAggregationConfig?.data?.filter((option) => currentDataAggregation.includes(option.key)) || []
    )
    setSelectedJurisdiction(
      jurisdiction.filter((option) =>
        currentJurisdiction.map((id) => Number.parseInt(id)).includes(Number.parseInt(option.STATE_ID))
      )
    )
    setSelectedTrendAnalysis(trendAnalysisFlag)
    setSelectedPersona(personaFlag)
    setSelectedChatbot(chatbotFlag)
    setSelectedChatbotExport(chatbotExportFlag || [0])
    setSelectedChatbotCopy(chatbotCopyFlag || [0])
    setSelectedExportToggle(exportFlag)
    setSelectedPOI(pointOfInterestsData.filter((option) => currentPOI.includes(Number.parseInt(option.POI_ID))))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    jurisdiction,
    currentJurisdiction,
    currentBenefits,
    currentWorkforces,
    currentFilters,
    currentDataAggregation,
    dataAggregationConfig,
    filterConfig,
    personaFlag,
    trendAnalysisFlag,
    chatbotFlag,
    chatbotExportFlag,
    chatbotCopyFlag,
    exportFlag,
    currentPOI,
    currentUseCases,
    pointOfInterestsData
  ])

  useEffect(() => {
    const isProjectNameBlank = projectName.trim().length === 0
    const selectedFilterKeys = selectedFilters
      .map((f) => f.FILTER_KEY)
      .sort((a, b) => a.localeCompare(b))
      .toString()
    const currentFilterKeys = [...currentFilters].sort((a, b) => a.localeCompare(b)).toString()

    if (
      projectNameChanged(projectName, currentGroupName, projectNamesList) ||
      projectDescriptionChanged(projectDescription, currentDescription) ||
      selectedFilterKeys !== currentFilterKeys ||
      selectedUseCasesChanged(selectedUseCases, currentUseCases) ||
      selectedBenefitsChanged(selectedBenefits, currentBenefits) ||
      selectedWorkforcesChanged(selectedWorkforces, currentWorkforces) ||
      selectedJurisdictionChanged(selectedJurisdiction, currentJurisdiction) ||
      selectedDataAggregationChanged(selectedDataAggregation, currentDataAggregation) ||
      selectedPersonaChanged(selectedPersona, personaFlag) ||
      selectedTrendAnalysisChanged(selectedTrendAnalysis, trendAnalysisFlag) ||
      selectedChatbotChanged(selectedChatbot, chatbotFlag) ||
      selectedChatbotExportChanged(selectedChatbotExport, chatbotExportFlag) ||
      selectedChatbotCopyChanged(selectedChatbotCopy, chatbotCopyFlag) ||
      selectedExportToggleChanged(selectedExportToggle, exportFlag) ||
      selectedPOIChanged(selectedPOI, currentPOI)
    ) {
      setSaveButtonDisabled(false)
    } else {
      setSaveButtonDisabled(true)
    }
    if (isCompleted) {
      if (
        isProjectNameBlank ||
        selectedFilters.length === 0 ||
        selectedJurisdiction.length === 0 ||
        selectedDataAggregation.length === 0 ||
        selectedPOI.length === 0
      ) {
        setSaveButtonDisabled(true)
      }
    }
    if (createProject) {
      if (isProjectNameBlank) {
        setSaveButtonDisabled(true)
      } else {
        setSaveButtonDisabled(false)
      }
    }
    if (isProjectNameBlank) {
      setSaveButtonDisabled(true)
    }
    const projectChanges = {
      projectDescription,
      currentDescription,
      selectedJurisdiction,
      currentJurisdiction,
      selectedDataAggregation,
      currentDataAggregation,
      selectedFilters,
      currentFilters,
      currentUseCases,
      selectedUseCases,
      selectedBenefits,
      currentBenefits,
      selectedWorkforces,
      currentWorkforces,
      currentGroupName,
      projectName,
      personaFlag,
      selectedPersona,
      selectedTrendAnalysis,
      trendAnalysisFlag,
      selectedChatbot,
      selectedChatbotExport,
      selectedChatbotCopy,
      chatbotFlag,
      chatbotExportFlag,
      chatbotCopyFlag,
      selectedExportToggle,
      exportFlag,
      currentPOI,
      selectedPOI
    }
    checkDetailsUpdated(setShowPopupOnNav, projectChanges)
  }, [
    selectedJurisdiction,
    selectedFilters,
    selectedUseCases,
    selectedBenefits,
    selectedWorkforces,
    selectedDataAggregation,
    selectedPersona,
    selectedTrendAnalysis,
    selectedChatbot,
    selectedChatbotExport,
    selectedChatbotCopy,
    selectedExportToggle,
    projectName,
    projectDescription,
    createProject,
    isCompleted,
    saveButtonDisabled,
    selectedPOI,
    currentGroupName,
    currentDescription,
    currentJurisdiction,
    currentDataAggregation,
    currentFilters,
    currentUseCases,
    currentBenefits,
    currentWorkforces,
    currentPOI,
    projectNamesList,
    personaFlag,
    trendAnalysisFlag,
    chatbotFlag,
    chatbotExportFlag,
    chatbotCopyFlag,
    exportFlag
  ])
  const [hiddenNotification, setHiddenNotification] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  useEffect(() => {
    setHiddenNotification(
      selectedFilters.length !== 0 &&
        selectedJurisdiction.length !== 0 &&
        selectedDataAggregation?.length !== 0 &&
        selectedPOI.length !== 0
    )
    if (selectedJurisdiction.length === 0) {
      setErrorMessage('Jurisdiction(s)')
    } else if (selectedDataAggregation?.length === 0) {
      setErrorMessage('Data Aggregation')
    } else if (selectedFilters.length === 0) {
      setErrorMessage('Select at least one filter from the filter categories')
    } else if (selectedPOI.length === 0) {
      setErrorMessage('Points of Interest')
    }
  }, [projectName, selectedFilters, selectedJurisdiction, selectedDataAggregation, selectedPOI, createProject])

  const handleClickOpen = () => {
    setIsSaving(false)
    setOpen(true)
  }
  const [showNotification, setShowNotification] = useState(false)
  const [showInfoAlert, setShowInfoAlert] = useState(false)

  useEffect(() => {
    if (
      !loading &&
      !createProjectLoading &&
      !createProjectError &&
      creategroup &&
      !showNotification &&
      createProject &&
      navigateToProjectList
    ) {
      setIsSaving(false)
      navigate(`/adminpanel/client/${clientId}`)
    }
  }, [
    creategroup,
    createProjectLoading,
    showNotification,
    clientId,
    createProject,
    createProjectError,
    loading,
    navigate,
    navigateToProjectList
  ])

  useEffect(() => {
    if (!loading && !error && !errorInFetching && navigateToProjectList && !showNotification) {
      setIsSaving(false)
      navigate(`/adminpanel/client/${clientId}`)
    }
  }, [
    loadingUpdateProject,
    loadingGroupPermission,
    loadingGroups,
    showNotification,
    clientId,
    error,
    errorInFetching,
    loading,
    navigate,
    navigateToProjectList
  ])

  // Reset isSaving when notification is shown and mutations are complete
  useEffect(() => {
    if (showNotification && !loadingUpdateProject && !createProjectLoading && isSaving) {
      setIsSaving(false)
    }
  }, [showNotification, loadingUpdateProject, createProjectLoading, isSaving])

  useEffect(() => {
    if (createProject) {
      setSaveButtonDisabled(projectName.trim().length === 0)
    }
  }, [createProject, projectName])
  const handleSave = (exit) => {
    setIsSaving(true)
    exit ? setPopupStyle('save and exit') : setPopupStyle('save')

    if (createProject) {
      const projectDetails = {
        exit,
        clientId,
        projectName,
        projectDescription,
        selectedFilters,
        selectedUseCases,
        selectedBenefits,
        selectedWorkforces,
        selectedJurisdiction,
        selectedDataAggregation,
        setShowNotification,
        setInputDisabled,
        selectedTrendAnalysis,
        selectedPersona,
        selectedChatbot,
        selectedExportToggle,
        selectedChatbotExport,
        selectedChatbotCopy,
        selectedPOI
      }
      saveCreateProject(projectDetails, handleClickOpen, setNavigateToProjectList, handleCreate)
    } else {
      const updateProjectDetails = {
        projectId,
        exit,
        projectName,
        setCurrentGroupName,
        currentDescription,
        projectDescription,
        currentJurisdiction,
        currentDataAggregation,
        currentFilters,
        selectedDataAggregation,
        selectedJurisdiction,
        selectedFilters,
        currentUseCases,
        selectedUseCases,
        selectedBenefits,
        currentBenefits,
        selectedWorkforces,
        currentWorkforces,
        setShowNotification,
        setInputDisabled,
        selectedTrendAnalysis,
        selectedChatbot,
        selectedPersona,
        selectedExportToggle,
        chatbotCopyFlag,
        chatbotExportFlag,
        selectedChatbotExport,
        selectedChatbotCopy,
        currentPOI,
        selectedPOI
      }
      saveUpdateProject(
        updateProjectDetails,
        handleUpdate,
        handleClickOpen,
        setNavigateToProjectList,
        false,
        refetchGroupPermission,
        refetchGroups
      )
    }
  }

  useEffect(() => {
    setSelectedUseCases([...getSelectedUseCases(updatedUseCases)])
  }, [updatedUseCases])

  const handleOptionDeletion = (type, data, option) => {
    setFilterToBeDeleted({type, data, option})
    const affectedUseCasesList = []
    for (const item of updatedUseCases || []) {
      for (const topic of item.topics) {
        handleUpdateSelectedUseCases(topic, affectedUseCasesList, option)
      }
    }
    setAffectedUseCases([...affectedUseCasesList])
    if (affectedUseCasesList.length > 0) {
      setShowUseCaseConfirmationDialog(true)
    } else {
      setSelectedFilters(selectedFilters.filter((f) => f.FILTER_ID !== option.FILTER_ID))
    }
  }

  const handleConfirmOptionDeletion = () => {
    const {option} = filterToBeDeleted || {}
    setSelectedFilters(selectedFilters.filter((f) => f.FILTER_ID !== option.FILTER_ID))
    setShowUseCaseConfirmationDialog(false)
    setShowInfoAlert(true)
  }

  const saveProjectDetails = (exit) => {
    //  make api call here to create group with createGroup Payload if createProject is true
    if (createProject) {
      saveCreateProjectDetails(setShowNotification)
      if (exit && !createProjectError) {
        setNavigateToProjectList(true)
      }
      !createProjectError && setInputDisabled(true)
    }
    //  make api call here to update group updateGroup Payload
    else {
      saveUpdateProjectDetails(
        setShowNotification,
        exit,
        setNavigateToProjectList,
        setInputDisabled,
        refetchGroupPermission,
        refetchGroups
      )
    }
  }
  const handleCancel = () => {
    if (showPopupOnNav) {
      setPopupStyle('cancel')
      handleClickOpen()
    } else {
      createProject && !group?.groupList.GROUPList.length
        ? navigate(`/adminpanel`)
        : navigate(`/adminpanel/client/${clientId}`)
    }
  }
  const handleUserViewSwitch = () => {
    if (showPopupOnNav) {
      setPopupStyle('userManagement')
      handleClickOpen()
    } else {
      setRenderUserView(true)
    }
  }

  const handleProjectBack = () => {
    if (!renderUserView && showPopupOnNav) {
      setOpen(true)
      setPopupStyle('back')
    } else {
      setGoBack(true)
    }
  }

  const scrollToJurisdiction = useRef(null)
  const scrollToHealth = useRef(null)
  const scrollToDataAggregation = useRef(null)
  const scrollToPointOfInterest = useRef(null)

  const refetchDetails = (event) => {
    event.preventDefault()
    refetchGroupPermission()
    refetchGroups()
  }

  const saveButtonDisableConditions = () => {
    return (
      showNotification ||
      saveButtonDisabled ||
      errorTextEnabled ||
      filterDetailsError ||
      errorLoadingStates ||
      loading ||
      loadingStates ||
      filterDetailsLoading
    )
  }
  const renderProjectView = () => {
    const projectDetails = {
      currentGroupName,
      currentDescription,
      currentFilters,
      currentJurisdiction,
      currentDataAggregation,
      currentBenefits,
      currentWorkforces,
      currentPOI,
      setProjectName,
      setProjectDescription,
      setSelectedFilters,
      setSelectedBenefits,
      setSelectedWorkforces,
      setSelectedJurisdiction,
      setSelectedDataAggregation,
      setInputDisabled,
      setErrorTextEnabled,
      setSelectedPOI,
      setSelectedUseCases,
      setSelectedTrendAnalysis,
      setSelectedPersona,
      setSelectedChatbot,
      setSelectedChatbotExport,
      setSelectedChatbotCopy,
      setSelectedExportToggle,
      trendAnalysisFlag,
      personaFlag,
      chatbotFlag,
      chatbotExportFlag,
      chatbotCopyFlag,
      exportFlag,
      dataAggregationConfig
    }
    const errorProjectDetails = {
      inputDisabled,
      setInputDisabled,
      errorTextEnabled,
      setErrorTextEnabled
    }
    return (
      <>
        {!createProject && projectId && (
          <ErrorNotificationComponent
            hidden={hiddenNotification}
            errorMessage={errorMessage}
            scrollToJurisdiction={scrollToJurisdiction}
            scrollToHealth={scrollToHealth}
            scrollToDataAggregation={scrollToDataAggregation}
            scrollToPointOfInterest={scrollToPointOfInterest}
          />
        )}
        {!projectId && <InfoNotificationComponent />}
        <ProjectDescriptionComponent
          projectName={projectName}
          currentProjectName={currentGroupName}
          setProjectName={setProjectName}
          projectDescription={projectDescription}
          setProjectDescription={setProjectDescription}
          create={createProject}
          setCreateProject={setCreateProject}
          errorProjectDetails={errorProjectDetails}
        />
        <NavigationBox>
          <NavigationItem selected={true}>Project permissions</NavigationItem>
          <NavigationItem selected={false} onClick={handleUserViewSwitch}>
            Users of this project
          </NavigationItem>
        </NavigationBox>

        {/* All Project Permissions Header with Expand/Collapse All */}
        <PermissionsHeader allExpanded={areAllSectionsExpanded} onToggleAll={handleToggleAllSections} />

        {/* GEOGRAPHIES Section Group */}
        <SectionGroupHeader label='GEOGRAPHIES' />

        <ProjectSectionAccordion
          title='Jurisdictions'
          sectionKey='jurisdictions'
          expanded={expandedSections.jurisdictions}
          onChange={handleAccordionChange('jurisdictions')}
          scrollRef={scrollToJurisdiction}
        >
          <JurisdictionComponent
            selectedData={selectedJurisdiction}
            setJurisdictionValue={setSelectedJurisdiction}
            enabled={true}
            isCompleted={createProject ? false : isCompleted}
          />
        </ProjectSectionAccordion>

        <ProjectSectionAccordion
          title='Data Granularity'
          sectionKey='dataGranularity'
          expanded={expandedSections.dataGranularity}
          onChange={handleAccordionChange('dataGranularity')}
          scrollRef={scrollToDataAggregation}
        >
          <DataAggregation
            selectedData={selectedDataAggregation}
            setSelectedDataAggregation={setSelectedDataAggregation}
            enabled={true}
            isCompleted={createProject ? false : isCompleted}
          />
        </ProjectSectionAccordion>

        {/* FILTERS Section Group */}
        <SectionGroupHeader label='FILTERS' />

        <ProjectSectionAccordion
          title='Individual Profile'
          sectionKey='individualProfile'
          expanded={expandedSections.individualProfile}
          onChange={handleAccordionChange('individualProfile')}
          scrollRef={scrollToHealth}
        >
          <IndividualProfileComponent
            selectedData={selectedFilters}
            setFilterValue={setSelectedFilters}
            enabled={true}
            isCompleted={createProject ? false : isCompleted}
            handleOptionDeletion={(type, data, option) => handleOptionDeletion(type, data, option)}
            hasAnyFilters={selectedFilters.length > 0}
          />
        </ProjectSectionAccordion>

        <ProjectSectionAccordion
          title='Individual Health'
          sectionKey='individualHealth'
          expanded={expandedSections.individualHealth}
          onChange={handleAccordionChange('individualHealth')}
        >
          <IndividualHealthFilterComponent
            selectedData={selectedFilters}
            setFilterValue={setSelectedFilters}
            enabled={true}
            isCompleted={createProject ? false : isCompleted}
            handleOptionDeletion={(type, data, option) => handleOptionDeletion(type, data, option)}
            hasAnyFilters={selectedFilters.length > 0}
          />
        </ProjectSectionAccordion>

        <ProjectSectionAccordion
          title='Financial Well-Being'
          sectionKey='financialWellbeing'
          expanded={expandedSections.financialWellbeing}
          onChange={handleAccordionChange('financialWellbeing')}
        >
          <FinancialWellbeingComponent
            selectedData={selectedFilters}
            setFilterValue={setSelectedFilters}
            enabled={true}
            isCompleted={createProject ? false : isCompleted}
            handleOptionDeletion={(type, data, option) => handleOptionDeletion(type, data, option)}
            hasAnyFilters={selectedFilters.length > 0}
          />
        </ProjectSectionAccordion>

        <ProjectSectionAccordion
          title='Home and Household'
          sectionKey='homeAndHousehold'
          expanded={expandedSections.homeAndHousehold}
          onChange={handleAccordionChange('homeAndHousehold')}
        >
          <HomeAndHouseholdFilterComponent
            selectedData={selectedFilters}
            setFilterValue={setSelectedFilters}
            enabled={true}
            isCompleted={createProject ? false : isCompleted}
            handleOptionDeletion={(type, data, option) => handleOptionDeletion(type, data, option)}
            hasAnyFilters={selectedFilters.length > 0}
          />
        </ProjectSectionAccordion>

        <ProjectSectionAccordion
          title='Resource Access Barriers and Support'
          sectionKey='resourceAccessBarriers'
          expanded={expandedSections.resourceAccessBarriers}
          onChange={handleAccordionChange('resourceAccessBarriers')}
        >
          <ResourceAccessBarriersComponent
            selectedData={selectedFilters}
            setFilterValue={setSelectedFilters}
            enabled={true}
            isCompleted={createProject ? false : isCompleted}
            handleOptionDeletion={(type, data, option) => handleOptionDeletion(type, data, option)}
            hasAnyFilters={selectedFilters.length > 0}
          />
        </ProjectSectionAccordion>

        <ProjectSectionAccordion
          title='Community Support and Location'
          sectionKey='communitySupport'
          expanded={expandedSections.communitySupport}
          onChange={handleAccordionChange('communitySupport')}
        >
          <CommunitySupportComponent
            selectedData={selectedFilters}
            setFilterValue={setSelectedFilters}
            enabled={true}
            isCompleted={createProject ? false : isCompleted}
            handleOptionDeletion={(type, data, option) => handleOptionDeletion(type, data, option)}
            hasAnyFilters={selectedFilters.length > 0}
          />
        </ProjectSectionAccordion>

        <ProjectSectionAccordion
          title='Environmental Exposure'
          sectionKey='environmentalExposure'
          expanded={expandedSections.environmentalExposure}
          onChange={handleAccordionChange('environmentalExposure')}
        >
          <EnvironmentalExposureComponent
            selectedData={selectedFilters}
            setFilterValue={setSelectedFilters}
            enabled={true}
            isCompleted={createProject ? false : isCompleted}
            handleOptionDeletion={(type, data, option) => handleOptionDeletion(type, data, option)}
            hasAnyFilters={selectedFilters.length > 0}
          />
        </ProjectSectionAccordion>

        <ProjectSectionAccordion
          title='Benefit Programs'
          sectionKey='benefitPrograms'
          expanded={expandedSections.benefitPrograms}
          onChange={handleAccordionChange('benefitPrograms')}
        >
          <Benefits benefits={benefits} selectedData={selectedBenefits} setSelectedData={setSelectedBenefits} />
        </ProjectSectionAccordion>

        <ProjectSectionAccordion
          title='Point of Interests (POIs)'
          sectionKey='pointOfInterests'
          expanded={expandedSections.pointOfInterests}
          onChange={handleAccordionChange('pointOfInterests')}
          scrollRef={scrollToPointOfInterest}
        >
          <PointOfInterest
            selectedData={selectedPOI}
            setPointofInterestValue={setSelectedPOI}
            enabled={true}
            isCompleted={createProject ? false : isCompleted}
          />
        </ProjectSectionAccordion>

        {/* FEATURE PERMISSIONS Section Group */}
        <SectionGroupHeader label='FEATURE PERMISSIONS' />

        <ProjectSectionAccordion
          title='Common Usecases'
          sectionKey='commonUsecases'
          expanded={expandedSections.commonUsecases}
          onChange={handleAccordionChange('commonUsecases')}
        >
          <UseCases
            useCaseConfigData={useCaseConfigData}
            selectedFilters={selectedFilters}
            setUpdatedUseCases={setUpdatedUseCases}
          />
        </ProjectSectionAccordion>

        <ProjectSectionAccordion
          title='Behavioral Persona'
          sectionKey='behavioralPersona'
          expanded={expandedSections.behavioralPersona}
          onChange={handleAccordionChange('behavioralPersona')}
        >
          <PersonaFlag selectedPersona={selectedPersona} setselectedPersona={setSelectedPersona} />
        </ProjectSectionAccordion>

        <ProjectSectionAccordion
          title='Trend Analysis'
          sectionKey='trendAnalysis'
          expanded={expandedSections.trendAnalysis}
          onChange={handleAccordionChange('trendAnalysis')}
        >
          <TrendAnalysisComponent selectedTrend={selectedTrendAnalysis} setselectedTrend={setSelectedTrendAnalysis} />
        </ProjectSectionAccordion>

        <ProjectSectionAccordion
          title='Export'
          sectionKey='export'
          expanded={expandedSections.export}
          onChange={handleAccordionChange('export')}
        >
          <ExportPermissionComponent
            selectedExportToggle={selectedExportToggle}
            setselectedExportToggle={setSelectedExportToggle}
          />
        </ProjectSectionAccordion>

        <ProjectSectionAccordion
          title='HealthPrism Agent'
          sectionKey='healthprismAgent'
          expanded={expandedSections.healthprismAgent}
          onChange={handleAccordionChange('healthprismAgent')}
        >
          <ChatbotComponent selectedChat={selectedChatbot} setSelectedChatbot={setSelectedChatbot} />
          <ChatbotExport
            selectedExport={selectedChatbotExport}
            setSelectedExport={setSelectedChatbotExport}
            disabled={selectedChatbot?.[0] === 0}
          />
          <ChatbotCopy
            selectedCopy={selectedChatbotCopy}
            setSelectedCopy={setSelectedChatbotCopy}
            disabled={selectedChatbot?.[0] === 0}
          />
        </ProjectSectionAccordion>

        {/* Workforces section - conditionally rendered */}
        {workforces.length > 0 && (
          <ProjectSectionAccordion title='Workforces' sectionKey='workforces' defaultExpanded={false}>
            <Workforces
              workforces={workforces}
              selectedData={selectedWorkforces}
              setSelectedData={setSelectedWorkforces}
            />
          </ProjectSectionAccordion>
        )}
        <StickyFooterBox>
          <Box display='flex' alignItems='center' ml={-1}>
            {!createProject && (
              <SaveButton
                data-testid='save-button'
                onClick={() => handleSave(false)}
                disabled={saveButtonDisableConditions()}
              >
                Save
              </SaveButton>
            )}
            <SaveAndExitButton
              data-testid='save-exit-button'
              onClick={() => handleSave(true)}
              disabled={saveButtonDisableConditions()}
            >
              Save & Exit
            </SaveAndExitButton>
            <CancelButton variant='contrastButton' data-testid='cancel-button' sx={{ml: 1}} onClick={handleCancel}>
              Cancel
            </CancelButton>
          </Box>
        </StickyFooterBox>
        {open && (
          <PopupComponent
            open={open}
            setOpen={setOpen}
            createProject={createProject}
            popupStyle={popupStyle}
            resetAll={resetAll}
            saveCurrent={saveProjectDetails}
            setGoBack={setGoBack}
            setRenderUserView={setRenderUserView}
            isCompleted={!createProject && isCompleted}
            projectDetails={projectDetails}
            toggleWarn={false}
          />
        )}
        <SnackbarComponent
          error={error || createProjectError}
          showNotification={showNotification}
          setShowNotification={setShowNotification}
          message={error || createProjectError ? SnackbarMessage.projectAddError : SnackbarMessage.pojectSaved}
        />
        <ConfirmationDialog
          selectedUseCases={affectedUseCases.map((obj) => obj.name)}
          showDialog={showUseCaseConfirmationDialog}
          handleConfirm={handleConfirmOptionDeletion}
          handleClose={() => {
            setShowUseCaseConfirmationDialog(false)
          }}
        />
        <UseCaseNotificationAlert
          message={
            <StyledFlexBox>
              <StyledNotificationInfoOutlinedIcon />
              <StyledNotificationTextBox>
                {affectedUseCases.map((obj) => obj.name).join(', ')}{' '}
                {affectedUseCases.length > 1 ? 'use cases' : 'use case'} automatically been removed!
              </StyledNotificationTextBox>
            </StyledFlexBox>
          }
          vertical='top'
          horizontal='center'
          open={showInfoAlert}
          setOpen={setShowInfoAlert}
        />
      </>
    )
  }
  const ariaLabel = renderUserView ? 'User Management page' : `${clientName}, ${projectName} details page`
  const renderRightPanel = () => {
    return (
      <RightPanel>
        <BreadcrumbsComponent
          props={!createProject && projectId ? navigationProps : createProjectNavProps}
          goBack={goBack}
          setOpen={setOpen}
          setPopupStyle={setPopupStyle}
          isProjectView={!renderUserView}
          showPopup={showPopupOnNav}
          create={createProject}
          hasProjects={group?.groupList.GROUPList.length}
          isClient={false}
          hideBackButton
        />
        <ProjectDetailsRow>
          <StyledBackButton onClick={handleProjectBack} aria-label='Go back'>
            <StyledBackIcon />
          </StyledBackButton>
          <StyledTypography variant='h5' tabIndex={0} aria-label={ariaLabel} title={clientName}>
            {renderUserView ? 'User Management' : clientName}
          </StyledTypography>
          {createProject && !renderUserView && <NewIcon>New</NewIcon>}
        </ProjectDetailsRow>
        <br />
        {renderUserView ? (
          <UserManagementComponent isCompleted={!createProject && isCompleted} setRenderUserView={setRenderUserView} />
        ) : (
          renderProjectView()
        )}
      </RightPanel>
    )
  }
  const getErrorMessage = () => {
    // const {message} = errorGroupPermissions ? errorGroupPermissions : errorGroups
    const {message} = errorGroupPermissions || errorGroups
    const errorClientProject = errorGroupPermissions && errorGroups
    const errorClient = `client id ${clientId} `
    const errorProject = `project id ${projectId} `
    const getError = errorGroupPermissions ? errorProject : errorClient
    const errorId = errorClientProject ? errorClient + 'and ' + errorProject : getError
    if (getStatusCode(message) === 404) {
      return `Sorry, we couldn't find that page with ${errorId}`
    } else if (getStatusCode(message) === 401) {
      return `Sorry, you are not authorized to access ${errorId}`
    }
  }

  const errorComponet = (code) => {
    return <ErrorComponent isAdminPanelOpen={isAdminPanelOpen} errorCode={code} errorMsg={getErrorMessage()} />
  }
  if (!loading && errorInFetching && !createProject) {
    // const {message} = errorGroupPermissions ? errorGroupPermissions : errorGroups
    const {message} = errorGroupPermissions || errorGroups
    const code = getStatusCode(message)
    return code === 404 || code === 401 ? (
      errorComponet(code)
    ) : (
      <StyledBoxDescription>
        <CenteredErrorContainer>
          <DataRefetchOnError onTryAgain={(event) => refetchDetails(event)} />
        </CenteredErrorContainer>
      </StyledBoxDescription>
    )
  }

  return (
    <StyledBoxDescription>
      {loading ? (
        <LoadingIcon title={loadingTitles.projectDetails} />
      ) : (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            {renderRightPanel()}
          </Grid>
        </Grid>
      )}
    </StyledBoxDescription>
  )
}

export default ProjectDetailsComponent

/**
 * ProjectDetailsComponent
 
  Function Description:
   - handleSave: Handles saving project details (create or update).
   - handleCancel: Handles canceling the current operation and navigating back.
   - handleOptionDeletion: Handles the deletion of selected options and updates dependent use cases.
   - handleConfirmOptionDeletion: Confirms the deletion of options and updates the state.
   - saveProjectDetails: Saves the project details to the backend.
   - refetchDetails: Refetches project details in case of errors.
   - saveButtonDisableConditions: Determines whether the save button should be disabled.
   - renderProjectView: Renders the main project details view.
   - renderRightPanel: Renders the right panel containing breadcrumbs and project details.
   - errorComponet: Renders error messages based on error codes.
  
  
   This component is responsible for rendering the details of a project in the admin panel.
   It provides functionality for creating, updating, and managing project details, including:
   - Project description
   - Jurisdiction
   - Data aggregation
   - Health conditions
   - Social determinants of health (SDOH)
   - Use cases
   - Benefits
   - Workforces
   - Points of interest (POI)
   - Persona flags
   - Trend analysis
   - Chatbot and export permissions
  
   The component also handles navigation, error handling, and state management for the project details.
  
   Props:
   @param {boolean} isAdminPanelOpen - Indicates whether the admin panel is open.
   @param {function} setIsAdminPanelOpen - Function to set the state of the admin panel.
  
   State Variables:
   - Various state variables are used to manage the current project details, selected options, and UI states.
  
   Hooks:
   - useEffect: Used for fetching data, updating states, and handling side effects.
   - useNavigate: Used for navigation within the application.
   - useParams: Used to extract route parameters (clientId, projectId).
  
   
   Error Handling:
   - Displays appropriate error messages for 404 and 401 errors.
   - Provides a retry mechanism for fetching data in case of errors.
  
   Navigation:
   - Handles navigation between different views (e.g., user management, project details).
   - Displays breadcrumbs for easy navigation.
  
   Loading and Notifications:
   - Displays loading indicators while fetching data.
   - Shows notifications for success or error states.
  
   Accessibility:
   - Provides ARIA labels for better accessibility.
 */
